import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import {
  formatInCompanyTz,
  formatEntryTime,
  toCompanyDatetimeLocal,
  companyDatetimeLocalToISO,
  companyTimezoneHint,
} from '@/lib/timesheetTime';
import { CompanyTimezoneHint } from '@/components/timesheets/CompanyTimezoneHint';
import { useHasPermission } from '@/hooks/permissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { CalendarDays, Clock, Edit, Check, X, RotateCcw, Edit2, Plus, BadgeCheck, RotateCw, Loader2, Download } from 'lucide-react';
import { ManagerTimesheetCard } from '@/components/cards/ManagerTimesheetCard';
import { CommentInfoIcon } from '@/components/timesheets/CommentInfoIcon';
import { BulkTimeEntryDialog } from '@/components/timesheets/BulkTimeEntryDialog';
import { TimesheetExportDialog } from '@/components/timesheets/TimesheetExportDialog';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import { 
  useCompanyProfiles, 
  useTimesheetMutations,
  type TimeEntryWithProfile,
  type TimesheetFilters 
} from '@/hooks/timesheets';
import { useAccumulatedCompanyTimeEntries } from '@/hooks/timesheets/useAccumulatedCompanyTimeEntries';
// Payroll filter options
type PayrollFilterOption = 'all' | 'verified' | 'rolled_back' | 'not_processed';

const PAGE_SIZE = 50;

const ManagerTimesheets = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const canApproveTimesheets = useHasPermission('approve_timesheets');
  const isMobile = useIsMobile();
  const timezone = useCompanyTimezone();
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [selectedPayrollStatus, setSelectedPayrollStatus] = useState<PayrollFilterOption>('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntryWithProfile | null>(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [newComment, setNewComment] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  // React Query hooks
  const filters: Omit<TimesheetFilters, 'page' | 'limit'> = {
    month: selectedMonth,
    employeeId: selectedEmployee,
    payrollStatus: selectedPayrollStatus !== 'all' ? selectedPayrollStatus : undefined,
    timezone,
  };

  // Cache-derived accumulator: one TanStack query per loaded page, each
  // independently refetched on invalidation. Replaces the previous local
  // `accumulatedEntries` state that froze status changes on already-rendered
  // rows.
  const {
    entries: accumulatedEntries,
    totalCount,
    hasMore,
    isLoadingFirstPage: loading,
    isFetchingAny: isFetching,
  } = useAccumulatedCompanyTimeEntries(filters, currentPage + 1, PAGE_SIZE);

  const { data: profiles = [] } = useCompanyProfiles();
  const { 
    approveTimeEntry, 
    revertTimeEntry, 
    editTimeEntry
  } = useTimesheetMutations();

  // Reset pagination when filters change
  const handleFilterChange = useCallback((setter: (val: string) => void, value: string) => {
    setter(value);
    setCurrentPage(0);
  }, []);

  const handleMonthChange = useCallback((date: Date) => {
    setSelectedMonth(date);
    setCurrentPage(0);
  }, []);

  const handleLoadMore = useCallback(() => {
    setCurrentPage(prev => prev + 1);
  }, []);

  // Handler functions using React Query mutations
  const handleApproval = (entryId: string, status: 'approved' | 'rejected') => {
    approveTimeEntry.mutate({ entryId, status });
  };

  const handleRevert = (entryId: string) => {
    revertTimeEntry.mutate(entryId);
  };

  const handleEdit = () => {
    if (!editingEntry) return;

    const updateData: any = { entryId: editingEntry.id };
    
    // Only include fields that have changed
    if (editClockIn !== toCompanyDatetimeLocal(editingEntry.clock_in, timezone)) {
      updateData.clockIn = companyDatetimeLocalToISO(editClockIn, timezone);
    }
    
    if (editClockOut !== (editingEntry.clock_out ? toCompanyDatetimeLocal(editingEntry.clock_out, timezone) : '')) {
      updateData.clockOut = companyDatetimeLocalToISO(editClockOut, timezone);
    }

    // Handle new comment
    if (newComment.trim()) {
      updateData.addComment = newComment.trim();
    }

    // Validate that clock in is before clock out
    if (editClockIn && editClockOut && new Date(editClockIn) >= new Date(editClockOut)) {
      alert('Clock in time must be before clock out time');
      return;
    }

    editTimeEntry.mutate(updateData, {
      onSuccess: () => {
        setEditDialogOpen(false);
        setEditingEntry(null);
        setEditClockIn('');
        setEditClockOut('');
        setNewComment(''); // Clear new comment
      }
    });
  };

  const openEditDialog = (entry: TimeEntryWithProfile) => {
    setEditingEntry(entry);
    setEditClockIn(toCompanyDatetimeLocal(entry.clock_in, timezone));
    setEditClockOut(entry.clock_out ? toCompanyDatetimeLocal(entry.clock_out, timezone) : '');
    setNewComment(''); // Reset new comment
    setEditDialogOpen(true);
  };

  const calculateDuration = (clockIn: string, clockOut: string | null) => {
    if (!clockOut) return 'In Progress';
    
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  // Entries are now server-side filtered (including payroll status)
  const filteredEntries = accumulatedEntries;

  if (loading && currentPage === 0 && accumulatedEntries.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Team Timesheets</h1>
        <p className="text-muted-foreground">Manage and approve your team's time entries</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Time Entries
            </CardTitle>
            <CompanyTimezoneHint />
            
            {/* Filters Section */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Month Picker */}
              <div className="flex items-center gap-2">
                <Label className="whitespace-nowrap">Month:</Label>
                <MonthYearPicker
                  selectedDate={selectedMonth}
                  onDateChange={handleMonthChange}
                  fromYear={2020}
                  toYear={2030}
                  className="w-48"
                />
              </div>

{/* Employee Filter */}
              <div className="flex items-center gap-2">
                <Label htmlFor="employee-filter" className="whitespace-nowrap">Employee:</Label>
                <Select value={selectedEmployee} onValueChange={(val) => handleFilterChange(setSelectedEmployee, val)}>
                  <SelectTrigger className="w-full min-w-0 sm:w-48">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payroll Status Filter */}
              <div className="flex items-center gap-2">
                <Label htmlFor="payroll-filter" className="whitespace-nowrap">Payroll:</Label>
                <Select value={selectedPayrollStatus} onValueChange={(val) => handleFilterChange(setSelectedPayrollStatus as (val: string) => void, val)}>
                  <SelectTrigger className="w-full min-w-0 sm:w-40">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="rolled_back">Rolled Back</SelectItem>
                    <SelectItem value="not_processed">Not Processed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {canApproveTimesheets && (
                  <Button 
                    variant="outline" 
                    onClick={() => setBulkDialogOpen(true)}
                    className="whitespace-nowrap"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Bulk Entry
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setExportDialogOpen(true)}
                  className="whitespace-nowrap"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            <div className="space-y-4">
              {filteredEntries.map((entry) => (
                <ManagerTimesheetCard
                  key={entry.id}
                  entry={entry}
                  onApproval={handleApproval}
                  onRevert={handleRevert}
                  onEdit={openEditDialog}
                  calculateDuration={calculateDuration}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  canRevert={canApproveTimesheets}
                  isApprovalPending={approveTimeEntry.isPending}
                  isRevertPending={revertTimeEntry.isPending}
                />
              ))}
              {filteredEntries.length === 0 && (
                <div className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Clock className="h-8 w-8" />
                    <p>No time entries found</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Total Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                     <TableCell>
                       <div className="flex items-center gap-2">
                         <Avatar className="h-8 w-8">
                           <AvatarImage 
                             src={entry.profile?.avatar_url} 
                             alt={generateDisplayName(entry.profile?.first_name, entry.profile?.last_name)}
                           />
                           <AvatarFallback>
                             {generateInitials(entry.profile?.first_name, entry.profile?.last_name)}
                           </AvatarFallback>
                         </Avatar>
                         <div className="flex flex-col gap-1">
                           <span>
                             {entry.profile ? (
                               `${entry.profile.first_name ?? ''} ${entry.profile.last_name ?? ''}`.trim() || 'Unnamed'
                             ) : (
                               <span className="italic text-muted-foreground">Former employee</span>
                             )}
                           </span>
                           {entry.entry_type === 'manual' && (
                             <Badge variant="outline" className="text-xs w-fit">
                               <Edit2 className="h-3 w-3 mr-1" />
                               Manual
                             </Badge>
                           )}
                         </div>
                         <CommentInfoIcon 
                           entryId={entry.id} 
                           hasComments={entry.has_comments || false}
                         />
                       </div>
                    </TableCell>
                    <TableCell>
                      {formatInCompanyTz(entry.clock_in, timezone, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {formatEntryTime(entry.clock_in, timezone)}
                    </TableCell>
                    <TableCell>
                      {entry.clock_out ? formatEntryTime(entry.clock_out, timezone) : '-'}
                    </TableCell>
                    <TableCell>
                      {calculateDuration(entry.clock_in, entry.clock_out)}
                    </TableCell>
<TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={getStatusBadgeVariant(entry.status)}>
                          {entry.status}
                        </Badge>
                        {entry.payroll_status === 'verified' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                            <BadgeCheck className="h-3 w-3 mr-1" />
                            Payroll Verified
                          </Badge>
                        )}
                        {entry.payroll_status === 'rolled_back' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                            <RotateCw className="h-3 w-3 mr-1" />
                            Rolled Back
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {entry.status === 'pending' && entry.profile && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproval(entry.id, 'approved')}
                              disabled={approveTimeEntry.isPending}
                              className="h-8 w-8 p-0"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproval(entry.id, 'rejected')}
                              disabled={approveTimeEntry.isPending}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                        {(entry.status === 'approved' || entry.status === 'rejected') && canApproveTimesheets && entry.profile && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRevert(entry.id)}
                            disabled={revertTimeEntry.isPending}
                            className="h-8 w-8 p-0"
                            title="Revert to pending"
                          >
                            <RotateCcw className="h-4 w-4 text-orange-600" />
                          </Button>
                        )}
                        {entry.profile ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(entry)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">Read-only</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Clock className="h-8 w-8" />
                        <p>No time entries found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Load More button */}
          {hasMore && (
            <div className="flex items-center justify-center pt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isFetching}
                className="w-full sm:w-auto"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  `Load More (${filteredEntries.length} of ${totalCount})`
                )}
              </Button>
            </div>
          )}

          {!hasMore && filteredEntries.length > 0 && (
            <p className="text-center text-sm text-muted-foreground pt-4">
              Showing all {totalCount} entries
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <p className="text-xs text-muted-foreground">{companyTimezoneHint(timezone)}</p>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="clock-in">Clock In</Label>
              <Input
                id="clock-in"
                type="datetime-local"
                value={editClockIn}
                onChange={(e) => setEditClockIn(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="clock-out">Clock Out</Label>
              <Input
                id="clock-out"
                type="datetime-local"
                value={editClockOut}
                onChange={(e) => setEditClockOut(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-comment">Add Comment (Optional)</Label>
              <Input
                id="new-comment"
                type="text"
                placeholder="Add a comment to the history..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Time Entry Dialog */}
      <BulkTimeEntryDialog 
        open={bulkDialogOpen} 
        onOpenChange={setBulkDialogOpen} 
      />

      {/* Export Dialog */}
      <TimesheetExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        profiles={profiles}
        defaultMonth={selectedMonth}
        defaultEmployeeId={selectedEmployee}
      />
    </div>
  );
};

export default ManagerTimesheets;
